/**
 * Guide Article Page
 *
 * Individual guide article page with markdown rendering, breadcrumbs, CTA buttons
 */

import { useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  Container,
  Title,
  Text,
  Stack,
  Group,
  Button,
  Breadcrumbs,
  Anchor,
  Divider,
  Box,
} from '@mantine/core';
import { IconArrowLeft } from '@tabler/icons-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Layout } from '../../shared/components';
import { useSEO } from '../../shared/hooks';
import { getGuideBySlug } from '../../data/guide/guideData';
import type { CTAButton } from '../../data/guide/guideTypes';

/**
 * Guide Article Page Component
 *
 * Displays a single guide article with full content and CTAs
 */
export function GuideArticle() {
  const { slug } = useParams<{ slug: string }>();

  // Get article by slug
  const article = useMemo(() => {
    if (!slug) return undefined;
    return getGuideBySlug(slug);
  }, [slug]);

  // Set page title
  useSEO({
    title: article?.title || 'Przewodnik',
    description: article?.excerpt,
  });

  // 404 if article not found
  if (!slug || !article) {
    return (
      <Layout isAuthenticated={false}>
        <Container size="md" py={80}>
          <Stack gap="md" align="center">
            <Title order={1}>Nie znaleziono artykułu</Title>
            <Text c="dimmed">Artykuł o podanym adresie nie istnieje.</Text>
            <Button component={Link} to="/przewodnik/analizy" leftSection={<IconArrowLeft size={16} />}>
              Powrót do przewodnika
            </Button>
          </Stack>
        </Container>
      </Layout>
    );
  }

  // Format date
  const formattedDate = new Date(article.date).toLocaleDateString('pl-PL', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  // Breadcrumbs items
  const breadcrumbItems = [
    { title: 'Strona główna', href: '/' },
    { title: 'Przewodnik', href: '/przewodnik/analizy' },
    { title: article.title, href: `/przewodnik/analizy/${article.slug}` },
  ].map((item, index) => (
    <Anchor
      key={index}
      component={Link}
      to={item.href}
      c={index === 2 ? 'dimmed' : undefined}
    >
      {item.title}
    </Anchor>
  ));

  return (
    <Layout isAuthenticated={false}>
      <Container size="md" py={80}>
        <Stack gap="xl">
          {/* Breadcrumbs */}
          <Breadcrumbs separator="›">{breadcrumbItems}</Breadcrumbs>

          {/* Article Header */}
          <Stack gap="md">
            <Title order={1} size="h2">
              {article.title}
            </Title>

            <Group gap="md">
              <Text size="sm" c="dimmed">
                {formattedDate}
              </Text>
              <Text size="sm" c="dimmed">
                •
              </Text>
              <Text size="sm" c="dimmed">
                {article.author}
              </Text>
              <Text size="sm" c="dimmed">
                •
              </Text>
              <Text size="sm" c="dimmed">
                {article.readTime} czytania
              </Text>
            </Group>
          </Stack>

          <Divider />

          {/* Article Content */}
          <Box
            style={{
              fontSize: '1.125rem',
              lineHeight: 1.7,
            }}
          >
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={{
                h1: ({ children }) => (
                  <Title order={1} size="h2" mb="lg" mt="xl">
                    {children}
                  </Title>
                ),
                h2: ({ children }) => (
                  <Title order={2} size="h3" mb="md" mt="xl">
                    {children}
                  </Title>
                ),
                h3: ({ children }) => (
                  <Title order={3} size="h4" mb="sm" mt="lg">
                    {children}
                  </Title>
                ),
                p: ({ children }) => (
                  <Text mb="md" style={{ lineHeight: 1.7 }}>
                    {children}
                  </Text>
                ),
                a: ({ href, children }) => (
                  <Anchor component={Link} to={href || '#'} c="blue">
                    {children}
                  </Anchor>
                ),
                ul: ({ children }) => (
                  <ul style={{ marginBottom: '1rem', paddingLeft: '1.5rem' }}>{children}</ul>
                ),
                ol: ({ children }) => (
                  <ol style={{ marginBottom: '1rem', paddingLeft: '1.5rem' }}>{children}</ol>
                ),
                li: ({ children }) => (
                  <li style={{ marginBottom: '0.5rem' }}>{children}</li>
                ),
                hr: () => <Divider my="xl" />,
              }}
            >
              {article.content}
            </ReactMarkdown>
          </Box>

          {/* CTA Buttons */}
          {article.ctaButtons && article.ctaButtons.length > 0 && (
            <>
              <Divider />
              <Group gap="md" justify="center">
                {article.ctaButtons.map((cta: CTAButton, index: number) => (
                  <Button
                    key={index}
                    component={Link}
                    to={cta.url}
                    variant={cta.variant}
                    color={cta.color}
                    size="lg"
                  >
                    {cta.text}
                  </Button>
                ))}
              </Group>
            </>
          )}

          {/* Back to Guide Button */}
          <Divider />
          <Button
            component={Link}
            to="/przewodnik/analizy"
            variant="light"
            color="blue"
            leftSection={<IconArrowLeft size={16} />}
            size="md"
          >
            Powrót do przewodnika
          </Button>
        </Stack>
      </Container>
    </Layout>
  );
}
